import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { REACT_APP_SUPPLIER_PROFILE_BASE_URL } from '../../config/settings/environment'
import { Button, DataTable, Select, Typography, useSelect } from '@chilecompra/react-kit/components'
import { onSuppliersThunk, onSuppliersDetailsThunk } from './Suppliers.actions'
import exportToImage, { exportToCsv } from '../../modules/utils/exports'
import { formatAmount } from '../../modules/utils/formatters'
import truncate from '../../modules/utils/limiter'
import generateYearsBetween from '../../modules/utils/years'
import IconButton from '../../components/Buttons/IconButton'
import Infobar from '../../components/Infobar/Infobar'
import FichaSuppliers from '../../components/Infographics/FichaSuppliers'
import SuppliersNoInfo from '../../components/Errors/SuppliersNoInfo'
import {
  Card,
  CardAmount,
  CardHeading,
  CardMore,
  Wrapper,
  Exportable,
  GridWrapper,
  Link,
  InlineLabel,
  Strong
} from './Suppliers.styles'

/**
 * The Suppliers' container.
 */
const Suppliers = () => {
  const dispatch = useDispatch()
  const { latestUpdate } = useSelector(state => state.topNavbar)
  const { idEntity, name } = useSelector(state => state.buyerSection)
  const { endYear, loading: initialLoading, startYear } = useSelector(state => state.topNavbar)
  const years = generateYearsBetween(endYear, startYear)
  const [showTable, setShowTable] = useState(false)
  const [expandArrow, setExpandArrow] = useState(false)
  const [fileName, setFileName] = useState(`Proveedores_mas_transados_${years[0].value}`)
  const dataTableRef = useRef()
  const {
    data,
    detail: { csvStructure, detailList, totalAmount, totalPO },
    fetching: loading
  } = useSelector(state => state.suppliers)
  const [supplier, setSupplier] = useState({})
  const [supplierURL, setSupplierURL] = useState('')
  const { value: yearsValue, onChange: handleChange } = useSelect({
    initialValue: years[0].value,
    changeCallback: updatedValue => {
      dataTableRef.current?.setPage(1)
      dispatch(onSuppliersThunk(updatedValue, idEntity))
      setFileName('Proveedores_mas_transados' + updatedValue)
    }
  })
  const handleDetailSupplier = id => () => {
    const singleSup = data.find(item => item.rutProvider === id)

    dispatch(onSuppliersDetailsThunk(yearsValue, idEntity, singleSup.rutProvider))
    setShowTable(true)
    setSupplier(singleSup)
    setSupplierURL(`${REACT_APP_SUPPLIER_PROFILE_BASE_URL}/${singleSup.rutProvider}`)
  }
  const handleReturn = () => setShowTable(false)
  const handleOpenProfile = () => window.open(supplierURL, '_blank').focus()
  const handleCaptureImage = () => exportToImage('Suppliers', fileName)
  const handleDownloadFile = () => exportToCsv(csvStructure, fileName)

  useEffect(() => {
    if (!idEntity || initialLoading) {
      return
    }

    dispatch(onSuppliersThunk(yearsValue, idEntity))
  }, [initialLoading, idEntity])

  return (
    <Wrapper>
      <GridWrapper container justifyContent="center">
        <GridWrapper item xs={12} md={10}>
          <Typography variant="h2" fontSize="22px" fontWeight="bold">
            Proveedores con mayores montos transados
          </Typography>
          <Typography variant="body2" fontSize="16px" lineHeight="20px" margin="16px 0 48px">
            En esta sección podrás conocer cuáles son los proveedores que registran los mayores montos transados con{' '}
            <Strong>{name}</Strong> durante el año seleccionado. Recuerda que estos montos que se extraen de la orden de
            compra que emite una entidad en <Link href="https://www.mercadopublico.cl/Home">www.mercadopublico.cl</Link>
            , corresponden a compromisos de los organismos, no a pagos.
            <br />
            <br />
            La información de montos transados dentro de esta sección corresponde al monto bruto (monto neto +
            impuestos).
          </Typography>
        </GridWrapper>
      </GridWrapper>
      <GridWrapper container justifyContent="center">
        {!showTable ? (
          <>
            <GridWrapper container item align="left" md={8} xs={12}>
              <GridWrapper item align="left" md={3} xs={12}>
                <Select label="AÑO" options={years} value={yearsValue} onChange={handleChange} />
              </GridWrapper>
              <GridWrapper item align="left" md={5} xs={12} />
            </GridWrapper>
            <GridWrapper item align="left" md={2} xs={12} />
          </>
        ) : null}
        <GridWrapper item xs={12} md={10} margin="24px 0 0 0">
          {data.length >= 1 ? (
            <Typography fontSize="14px" fontWeight={showTable ? 'regular' : 'bold'} variant="body2" display="inline">
              Proveedores
            </Typography>
          ) : null}
          {showTable ? (
            <Typography fontSize="16px" fontWeight="bold" variant="body1" display="inline">
              {' / '}
              {supplier.provider}
            </Typography>
          ) : null}
        </GridWrapper>
        {showTable ? (
          <GridWrapper item xs={10} margin="32px 0 0 0">
            <GridWrapper container justifyContent="center">
              <GridWrapper item xs={12}>
                <IconButton type="back" onClick={handleReturn} fontWeight="bold" fontSize="14px">
                  <InlineLabel>Volver a los principales proveedores del año {yearsValue}</InlineLabel>
                </IconButton>
              </GridWrapper>
              <GridWrapper item xs={12} md={6} margin="32px 0 0 0">
                <Typography variant="body2" fontSize="16px" lineHeight="20px">
                  Monto total transado durante el año {yearsValue}:{' '}
                  <strong>${formatAmount(parseInt(totalAmount), 10)}</strong>
                </Typography>
                <Typography variant="body2" fontSize="16px" margin="8px 0 0 0">
                  Total de órdenes de compras: <strong>{totalPO}</strong>
                </Typography>
              </GridWrapper>
              <GridWrapper item xs={12} md={6} align="right" margin="32px 0 0 0">
                <Button color="primary" variant="contained" onClick={handleOpenProfile}>
                  Ver ficha del proveedor
                </Button>
              </GridWrapper>
            </GridWrapper>
            <GridWrapper container justifyContent="center">
              <GridWrapper item xs={12} margin="32px 0 0 0">
                <DataTable
                  headers={[
                    { label: 'Mes', value: 'month' },
                    { label: 'N° de órdenes de compra del mes', value: 'ponumber' },
                    { label: 'Monto transado en el mes', value: 'clpgrossAmount' }
                  ]}
                  headersCellsProps={[{ enableSort: true }, { enableSort: true }, { enableSort: true }]}
                  initialRows={10}
                  loading={loading}
                  records={detailList}
                  recordsDecorators={[
                    ({ month }) => month,
                    ({ ponumber }) => ponumber,
                    ({ clpgrossAmount }) => `$${formatAmount(parseInt(clpgrossAmount), 10)}`
                  ]}
                  ref={dataTableRef}
                />
              </GridWrapper>
            </GridWrapper>
          </GridWrapper>
        ) : null}
      </GridWrapper>
      {showTable ? null : (
        <Exportable id="Suppliers">
          <GridWrapper container justifyContent="center" padding="16px 0 16px 0">
            {data.length >= 1 ? (
              <GridWrapper item xs={12} md={10}>
                <GridWrapper container justifyContent="space-between" spacing={1}>
                  {data.map((supplier, index) => (
                    <GridWrapper key={`${supplier.rutProvider}__${index}`} item xs={12} sm={6} md={3}>
                      <Card
                        enableHover
                        color="blank"
                        elevation="1"
                        onClick={handleDetailSupplier(supplier.rutProvider)}
                      >
                        <GridWrapper container justifyContent="center" padding="0">
                          <CardHeading fontSize="18px" fontWeight="bold" variant="h3">
                            {truncate(supplier.provider, 50, ' ...')}
                          </CardHeading>
                          <CardAmount fontSize="12px" fontWeight="light" margin="8px 0" variant="body1">
                            ${formatAmount(supplier.annualCLPGrossAmount)}
                          </CardAmount>
                        </GridWrapper>
                        <CardMore fontSize="12px" variant="body2">
                          Ver detalle
                        </CardMore>
                      </Card>
                    </GridWrapper>
                  ))}
                </GridWrapper>
              </GridWrapper>
            ) : (
              <GridWrapper item xs={12} md={10}>
                <SuppliersNoInfo />
              </GridWrapper>
            )}
          </GridWrapper>
        </Exportable>
      )}
      <GridWrapper item xs={12} md={10} margin="32px auto 0 auto">
        <Infobar
          disableDownloadChart={showTable}
          disableDownloadCsv={!showTable}
          disableInfographic={showTable}
          expandArrow={expandArrow}
          infographic={<FichaSuppliers />}
          latestUpdate={latestUpdate}
          onCaptureImage={handleCaptureImage}
          onDownloadFile={handleDownloadFile}
          setExpandArrow={setExpandArrow}
        />
      </GridWrapper>
    </Wrapper>
  )
}

export default Suppliers
